// Shared role + building-type helpers.

// A team row is either a filled seat (has a real person) or an OPEN seat —
// a role the job needs but no one is in yet. Open seats carry this sentinel
// PersonName so they persist without a dedicated SharePoint column.
export const OPEN_SEAT = "(Open Seat)";
export const isOpenSeat = (m) => !m || !m.PersonName || /^\(open/i.test(m.PersonName);

// A "general superintendent" (incl. Gen. Finish Supt) is leadership, not a
// benchable field super. Catch gen/general anywhere alongside "sup".
export function isGeneralSupt(role) {
  const r = (role || "").toLowerCase();
  return /\bgen(\.|eral)?\b/.test(r) && /sup/.test(r);
}

// Group a company/project role into a family for filtering and staffing matches.
// PEs plan with the PM family, FEs with the superintendents, and QA/QC is its
// own family (e.g. a PM slot shows Sr PM / PM / APM / PE).
export function roleFamily(role) {
  const r = (role || "").toLowerCase();
  if (isGeneralSupt(role)) return "leadership";
  // QA/QC is its own family. Checked first because a "QA/QC Project Engineer"
  // title also contains "project engineer", which now maps to the PM family.
  if (/(qa|qc|quality)/.test(r)) return "qaqc";
  // PEs plan with the PM family; FEs plan with the superintendent family.
  if (/(sr\.?\s*pm|senior project manager|\bapm\b|assistant project manager|\bpm\b|project manager|project engineer|\bpe\b)/.test(r)) return "pm";
  if (/(sr\.?\s*supt|senior super|finish supt|asst\.?\s*supt|assistant super|\bsupt\b|superintendent|field engineer|\bfe\b)/.test(r)) return "supt";
  if (/(px|project executive)/.test(r)) return "px";
  if (/account/.test(r)) return "accountant";
  if (/(precon|estimat)/.test(r)) return "precon";
  if (/(director|vp|ceo|cfo|president)/.test(r)) return "leadership";
  return "other";
}

export const ROLE_GROUPS = [
  { key: "pm", label: "Project Managers" },
  { key: "supt", label: "Superintendents" },
  { key: "qaqc", label: "QA/QC" },
  { key: "px", label: "Project Executives" },
  { key: "precon", label: "Precon / Estimating" },
  { key: "accountant", label: "Accounting" },
  { key: "leadership", label: "Leadership" },
  { key: "other", label: "Other" },
];

export function peopleForRole(people, role) {
  const fam = roleFamily(role);
  return (people || []).filter((p) => roleFamily(p.CompanyRole) === fam);
}

// ── Staff groups (for coloring & grouping members on the project Gantt) ──
// QA/QC is its own group; PMs and field supers split out.
export function staffGroup(role) {
  const r = (role || "").toLowerCase();
  if (/carpenter|carpentry/.test(r)) return { key: "carp", label: "Carpenters", color: "#a05a2c", order: 7 };
  if (/qa|qc/.test(r)) return { key: "qaqc", label: "QA/QC", color: "#c0398b", order: 4 };
  const fam = roleFamily(role);
  if (fam === "pm") return { key: "pm", label: "PM Staff", color: "#3b6fb5", order: 1 };
  if (fam === "supt") return { key: "field", label: "Field Staff", color: "#d98f00", order: 2 };
  if (fam === "px") return { key: "px", label: "Project Executive", color: "#9b59b6", order: 5 };
  if (fam === "accountant") return { key: "acct", label: "Accounting", color: "#1f9b9b", order: 6 };
  return { key: "other", label: "Other", color: "#6b7280", order: 9 };
}

// ── Position buckets (for grouping the Assignments roster, like People) ──
export function positionBucket(role) {
  const r = (role || "").toLowerCase();
  if (/(sr\.?\s*pm|senior project manager)/.test(r)) return "Senior Project Managers";
  if (/(apm|assistant project manager)/.test(r)) return "Assistant Project Managers";
  if (/(\bpm\b|project manager)/.test(r)) return "Project Managers";
  if (/(sr\.?\s*supt|senior super)/.test(r)) return "Senior Superintendents";
  if (/finish\s*sup/.test(r)) return "Finish Superintendents";
  if (/(asst\.?\s*supt|assistant super)/.test(r)) return "Assistant Superintendents";
  if (/(supt|superintendent)/.test(r)) return "Superintendents";
  if (/qa|qc/.test(r)) return "QA/QC Project Engineers";
  if (/(project engineer|\bpe\b|field engineer|\bfe\b|engineer)/.test(r)) return "Project Engineers";
  return "Other";
}
export const POSITION_ORDER = [
  "Senior Project Managers", "Project Managers", "Assistant Project Managers",
  "Senior Superintendents", "Superintendents", "Finish Superintendents", "Assistant Superintendents",
  "Project Engineers", "QA/QC Project Engineers", "Other",
];

// ── Building types → Gantt bar colors ──
export const BUILDING_TYPES = ["Multifamily", "Commercial", "Tenant Improvement", "Student Housing", "Other"];

const BUILDING_COLORS = {
  "multifamily": "#3b6fb5",
  "commercial": "#3db87a",
  "tenantimprovement": "#d98f00",
  "studenthousing": "#9b59b6",
  "other": "#6b7280",
};

export function buildingColor(type) {
  const key = (type || "").toLowerCase().replace(/[^a-z]/g, "");
  return BUILDING_COLORS[key] || BUILDING_COLORS.other;
}
